package Structs

import DataBase.UINKDBInterface
import Utils.*
import kotlinx.serialization.SerialInfo
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.*
import myName
import printPlat

/** base/wrapped ≈ rate. Exact equality fails for fractions such as 0.4 × 29 / 0.4. */
fun wrappedRatioMatches(base: Float, wrapped: Float, rate: Float): Boolean {
    if (wrapped == 0f || rate <= 0f) return false
    val tol = kotlin.math.max(kotlin.math.abs(rate), 1f) * 0.001f
    return kotlin.math.abs(base / wrapped - rate) <= tol
}

data class PricedRow(val cp: Float?, val cd: Float?, val cu: Int?, val ci: Int? = null)

@myName("ProductDelivery")
@Serializable
data class ProductDelivery(
    @SerialName("id") var productId: Int,
    @SerialName("v") var value: Float = 0f,
    @SerialName("r") var returns: Float = 0f,
    @SerialName("p") var price: Float,
    @SerialName("sup") var supplier_id: Int = -1,
    @SerialName("u") var use_price: Byte = 0, // 0 dont use , 1 use - no tax product, 2 use - not included,3-use included
    @SerialName("utn") var use_price_tax_note: Byte = 0, // 0 dont use , 1 use - no tax product, 2 use - not included,3-use included
    @SerialName("d") var discount: Float = 0f,


    @SerialName("wv")
    //this is for orders, second amount
    var wrapped_amount: Float = 0f,

    @SerialName("wr")
    //this is for orders, second amount
    var wrapped_amount_return: Float = 0f,

    // conversion_ratio between the second amount to the actual value 1 value * conversion_ratio = wrapped_amount
    @SerialName("cr") var conversion_ratio: Float = 0f,
    // conversion_ratio between the second amount to the actual value 1 value * conversion_ratio = wrapped_amount
    @SerialName("wu") var wrapped_unit: Int = 0,

    // order-only third unit (no conversion). Display-only; never charged/counted in inventory.
    @SerialName("tv") var third_amount: Float = 0f,
    @SerialName("tu") var third_unit: Int = 0,

    @SerialName("n") var notes: String? = null,
    @SerialName("n2") var notes2: String? = null,
    @SerialName("n3") var notes3: String? = null,
    @SerialName("s") var doneState: Byte = 0,
    @SerialName("cost") var cost: Float? = null,
    @SerialName("o") var tempPosition: Int = 0,
    @SerialName("mek") var nmekulkal: Int = 0,
    @SerialName("o_locked") var o_locked: Int = 0,
    /** User tapped clear (X): row highlighted red; persisted in draft JSON as "zm" (1 = true, 0 = false). */
    @SerialName("zm") var amountClearedMark: Int = 0,
    @SerialName("cp") var cp: Float? = null,
    @SerialName("cu") var cu: Int? = null,
    @SerialName("cd") var cd: Float? = null,
    /** Priced API: 0 = delivery line u (use_price) or order p>0; utn+paid keeps price-flow id. */
    @SerialName("ci") var ci: Int? = null,

    ) {
    var isClient: Boolean = true

    var useWrapped: Byte = 0
    var changedPrice: Byte = 1
    var priceBefore: Float? = null
    var priceAfter: Float? = null

    @myName("getUnit")
    fun getUnit(force_above: Boolean?=null): WrappedName {
        var x: WrappedName? = null
        val useExtraUnit = wrapped_amount > 0 || wrapped_amount_return > 0 || force_above == true || estimatedWvca() > 0f
        if (useExtraUnit && wrapped_unit == 0) {
            x = getProductOrNull()?.effectiveExtraUnit()?.takeIf { it > 0 }?.let { WrappedName.NONE.convert(it) }
        }

        return x ?: WrappedName.NONE.convert(wrapped_unit)
    }

    /** Order-only third unit name (display-only; never charged/counted). */
    @myName("getThirdUnit")
    fun getThirdUnit(): WrappedName {
        if (third_unit != 0) return WrappedName.NONE.convert(third_unit)
        return getProductOrNull()?.effectiveThirdUnit()?.let { WrappedName.NONE.convert(it) } ?: WrappedName.NONE
    }

    @myName("getProduct")
    fun getProduct(): Product {
        return getProductOrNull()!!
    }

    fun getProductOrNull(): Product? {
        return if (isClient) UINKDBInterface.activeDB.getClientProduct(productId).first
        else UINKDBInterface.activeDB.getSupplierProduct(productId).first
    }

    /** Second-unit conversion: line `cr` if positive, else catalog rate. Never throws if the product is missing. */
    fun conversionForSecondAmount(): Float {
        if (conversion_ratio > 0f) return conversion_ratio
        return getProductOrNull()?.effectiveConversionRate() ?: 0f
    }

    /**
     * Wrapped qty is billed only when [conversion_ratio] is positive.
     * `cr == 0` means independent second unit (display only, not multiplied).
     * Conversion is never negative — treat any leftover stored `cr < 0` as 0.
     * Same rule as [DataBase.NKDBInterface.calcDeliveryNoteSumMap] (`wrapped * cr > 0`).
     */
    fun convertsWrappedToCharge(): Boolean = conversion_ratio > 0f

    fun chargeablePositiveQuantity(): Float =
        if (convertsWrappedToCharge()) value + wrapped_amount * conversion_ratio else value

    fun chargeableReturnQuantity(): Float =
        if (convertsWrappedToCharge()) returns + wrapped_amount_return * conversion_ratio else returns

    @myName("chargeableQuantity")
    fun chargeableQuantity(): Float = chargeablePositiveQuantity() - chargeableReturnQuantity()

    @myName("getOrderProduct")
    fun getOrderProduct(): OrderProduct? {
        return if (isClient) UINKDBInterface.activeDB.getOrderProduct(productId)?.first() else null
    }

    /**
     * Estimated wrapped (second unit) quantity — same as ApiOptimizedData `_estimated_wvca_sql` / notes `wvca`.
     */
    @myName("estimatedWvca")
    fun estimatedWvca(): Float {
        val orderCr = getProductOrNull()?.effectiveConversionRate() ?: 0f
        val inferred = when {
            wrapped_amount > 0f && value > 0f && conversion_ratio > 0f -> value / conversion_ratio
            wrapped_amount == 0f && value > 0f && orderCr > 0f -> value / orderCr
            else -> 0f
        }
        return wrapped_amount + inferred
    }

    @myName("getSupplier")
    fun getSupplier(): Supplier? {
        return if (supplier_id != -1) UINKDBInterface.activeDB.getSupplier(supplier_id).first else null
    }

    @myName("discountCalculated")
    fun discountCalculated(): Float {
        return (1 - discount / 100)
    }

    @myName("usePrice")
    fun usePrice(note_used: Byte): Boolean {
        return use_price > 0 || (note_used == (1).toByte() && use_price_tax_note > 0)
    }

    @myName("isNoTax")
    fun isNoTax(note_used: Byte): Boolean {
        return (use_price == (1).toByte()) || (note_used == (1).toByte() && use_price_tax_note == (1).toByte())
    }

    @myName("taxNotIncluded")
    fun taxNotIncluded(note_used: Byte = 0): Boolean {
        return use_price == (2).toByte() || (note_used == (1).toByte() && use_price_tax_note == (2).toByte())
    }

    @myName("taxIncluded")
    fun taxIncluded(note_used: Byte = 0): Boolean {
        return use_price == (3).toByte() || (note_used == (1).toByte() && use_price_tax_note == (3).toByte())
    }

    @myName("empty")
    fun empty(): Boolean {
        var cond = value == 0f
        var cond2 = returns == 0f
        var cond3 = conversion_ratio == 0f || wrapped_amount == 0f
        var cond4 = conversion_ratio == 0f || wrapped_amount_return == 0f
        return cond && cond2 && cond3 && cond4

    }

    @myName("emptyOrder")
    fun emptyOrder(): Boolean {
        var cond = value == 0f
        var cond2 = wrapped_amount == 0f
        // order-only third unit (no conversion) is a legitimate order amount on its own,
        // even with no first/second unit set yet.
        var cond3 = third_amount == 0f
        return cond && cond2 && cond3

    }

    @myName("doCopy")
    fun doCopy(id: Int? = null): ProductDelivery {
        val d = copy(productId = id ?: productId)
        d.isClient = isClient
        d.changedPrice = changedPrice
        return d
    }

    @myName("negative")
    fun negative(): ProductDelivery {
        return copy(
            value = returns,
            returns = value,
            wrapped_amount = wrapped_amount_return,
            wrapped_amount_return = wrapped_amount
        )
    }

    fun sql(): String {
        return if (!empty()) "${productId}_s#${value}@${productId}_r#${returns}@"
        else ""
    }

    @myName("isCollected")
    fun isCollected(): Boolean {
        return doneState == (1).toByte()
    }

    /** Collector "חסר" line: marked missing (X chip), amounts cleared, persisted as `s`=2 in draft. */
    @myName("isMissing")
    fun isMissing(): Boolean {
        return doneState == (2).toByte()
    }

    fun compare(pd: ProductDelivery): Boolean {
        return pd.productId == productId && pd.value == value && pd.returns == returns && pd.price == price && pd.use_price == use_price && pd.discount == discount && pd.wrapped_amount == wrapped_amount && pd.conversion_ratio == conversion_ratio
    }
    fun safeToStr(f: Float): String {
        return if (f.toString()=="") "0" else f.toString()
    }

    fun toJson(): JsonObject {
        val map: MutableMap<String, JsonPrimitive> = mutableMapOf()
        map["id"] = JsonPrimitive(productId.toString())
        map["v"] = JsonPrimitive(safeToStr(value))
        map["r"] = JsonPrimitive(safeToStr(returns))
        map["p"] = JsonPrimitive(safeToStr(price))
        if (use_price > 0) {
            map["u"] = JsonPrimitive(use_price.toString())
        }
        if (use_price_tax_note > 0) {
            map["utn"] = JsonPrimitive(use_price_tax_note.toString())
        }
        if (discount != 0f) {
            map["d"] = JsonPrimitive(safeToStr(discount))
        }
        if (supplier_id != -1) map["sup"] = JsonPrimitive(supplier_id.toString())
        if (wrapped_amount != 0f) map["wv"] = JsonPrimitive(safeToStr(wrapped_amount))
        if (conversion_ratio > 0f) map["cr"] = JsonPrimitive(safeToStr(conversion_ratio))
        if (wrapped_amount_return != 0f) map["wr"] = JsonPrimitive(safeToStr(wrapped_amount_return))
        if (wrapped_unit != 0) map["wu"] = JsonPrimitive(wrapped_unit.toString())
        if (third_amount != 0f) map["tv"] = JsonPrimitive(safeToStr(third_amount))
        if (third_unit != 0) map["tu"] = JsonPrimitive(third_unit.toString())
        if (doneState != (0).toByte()) map["s"] = JsonPrimitive(doneState.toString())
        if (notes != null) {
            map["n"] = JsonPrimitive(prepareStrToJson(notes!!))
        }
        if (notes2 != null) {
            map["n2"] =JsonPrimitive(prepareStrToJson(notes2!!))
        }
        if (notes3 != null) {
            map["n3"] = JsonPrimitive(prepareStrToJson(notes3!!))
        }
        if (tempPosition != 0) {
            map["o"] = JsonPrimitive(tempPosition.toString())
        }
        if (nmekulkal==1){

            map["mek"] = JsonPrimitive(nmekulkal.toString())
        }
        if (o_locked==1){

            map["o_locked"] = JsonPrimitive(o_locked.toString())
        }
        if (amountClearedMark != 0) {
            map["zm"] = JsonPrimitive(amountClearedMark.toString())
        }

        return JsonObject(map)
    }


    companion object {
        @myName("createFromJson")
        fun createFromJson(js: String): List<ProductDelivery> {
                return (getJsonWithConfig().parseToJsonElement((js).replace("\\'", "'")) as JsonArray).map {
                    getJsonWithConfig().decodeFromJsonElement(serializer(), it)
            }
        }

        @myName("createUsedState")
        fun createUsedState(
            noTaxProduct: Int, noTaxClient: Int, pricesShowIncluded: Int, forceInclude: Boolean = false
        ): Byte {
            return when {
                noTaxProduct == 1 || noTaxClient == 1 -> {
                    1
                }

                pricesShowIncluded == 0 && !forceInclude -> {
                    2
                }

                pricesShowIncluded == 1 || forceInclude -> {
                    3
                }

                else -> {
                    0
                }

            }
        }

        fun compareProductDvList(
            pd1L: List<ProductDelivery>,
            pd2L: List<ProductDelivery>,
        ): Boolean {
            if (pd1L.size != pd2L.size) return false
            pd2L.forEach { pd2 ->
                if (pd1L.find { it.compare(pd2) } == null) return false
            }

            return true
        }

        fun toJsonArrayString(pd: List<ProductDelivery>): String {
            return if (pd.isEmpty()) {
                "[]"
            } else {
                val payload = pd.map {
                    Json.encodeToJsonElement(JsonObject.serializer(), it.toJson()).toString()
                }.joinToString(",")
                return "[$payload]"
            }

        }

        fun weightEstimate(pds: List<ProductDelivery>): Float {
            return pds.sumOf {
                (it.getOrderProduct()?.weight ?: 0f).toDouble() * (it.value + it.conversion_ratio * it.wrapped_amount)
            }.toFloat()
        }
        fun reduceToUniqueProducts(list: List<ProductDelivery>,by:String="id"): List<ProductDelivery> {
            return list.groupBy {
            when(by){
                "category"->it.getProduct().category
                "category2"->it.getProduct().category2
                else ->it.productId
            }

            }
                .map { (id, products) ->
                    // If there's only one product with this ID, just return it (optimization)
                    if (products.size == 1) {
                        products.first()
                    } else {
                        // Take the first product as a base for non-summed fields (price, name, etc.)
                        // You might want to decide logic for differing prices/discounts,
                        // but usually, the first one is safe or you take the average.
                        val base = products.first().copy()
                        base.isClient = products.first().isClient

                        // Sum the specific fields
                        base.value = products.sumOf { it.value.toDouble() }.toFloat()
                        base.returns = products.sumOf { it.returns.toDouble() }.toFloat()
                        base.wrapped_amount = products.sumOf { it.wrapped_amount.toDouble() }.toFloat()
                        base.wrapped_amount_return = products.sumOf { it.wrapped_amount_return.toDouble() }.toFloat()

                        // Optional: Check if conversion_ratio needs handling.
                        // Usually, it should be the same for the same product ID.

                        base
                    }
                }
        }
        fun kartonEstimate(pds: List<ProductDelivery>): Float {

            return roundToDecimals(pds.sumOf { pd ->
                (pd.getOrderProduct()?.volume?.let { if (it == 0f) 0f else (pd.value + pd.conversion_ratio * pd.wrapped_amount) / it }
                    ?: 0f).toDouble()
            }.toFloat(), 2)
        }

        fun toWrappedAmountDescribe(pds: List<ProductDelivery>): List<ProductDelivery> {
            val dvs: MutableList<ProductDelivery> = mutableListOf()
            pds.forEach {
                if (it.value > 0 || it.returns > 0) {
                    dvs.add(it)
                }
                if ((it.wrapped_amount > 0 || it.wrapped_amount_return > 0) && it.conversion_ratio > 0) {
                    val cppd = it.doCopy()
                    cppd.value = it.wrapped_amount
                    cppd.returns = it.wrapped_amount_return
                    cppd.useWrapped = 1
                    cppd.wrapped_unit = it.wrapped_unit
                    dvs.add(
                        cppd
                    )
                }
            }
            return dvs
        }

        private fun jsonPrimitiveToFloat(el: JsonElement?): Float? {
            if (el == null) return null
            val p = el as? JsonPrimitive ?: return null
            return p.content.toFloatOrNull()
        }

        private fun jsonPrimitiveToInt(el: JsonElement?): Int? {
            if (el == null) return null
            val p = el as? JsonPrimitive ?: return null
            val c = p.content
            return c.toIntOrNull() ?: c.toFloatOrNull()?.toInt()
        }

        /**
         * Fills [ProductDelivery.cp], [ProductDelivery.cu], [ProductDelivery.discount], [ProductDelivery.ci] from API [delivery_prices]
         * (`[{ "id", "cp", "cd", "cu", "ci" }, ...]`). `cd` is discount % (same role as JSON `d`).
         * `ci`: 0 if note `u`≥1 or order p>0; else client/master/price_control id (utn+paid ≠ 0).
         * Rows are keyed by product `id` only (no index coupling).
         */
        @OptIn(ExperimentalStdlibApi::class)
        @myName("mergeDeliveryPricesIntoLines")
        fun mergeDeliveryPricesIntoLines(lines: MutableList<ProductDelivery>, deliveryPricesJson: String?) {
            val js = deliveryPricesJson?.trim() ?: return
            if (js.isEmpty() || js == "null" || js == "[]") return
            val arr = try {
                getJsonWithConfig().parseToJsonElement(js.replace("\\'", "'")) as? JsonArray
            } catch (_: Exception) {
                null
            } ?: return
            if (arr.isEmpty()) return



            val byProductId = buildMap<Int, PricedRow> {
                for (el in arr) {
                    val obj = el as? JsonObject ?: continue
                    val id = jsonPrimitiveToInt(obj["id"]) ?: continue
                    put(
                        id,
                        PricedRow(
                            cp = jsonPrimitiveToFloat(obj["cp"]),
                            cd = jsonPrimitiveToFloat(obj["cd"]),
                            cu = jsonPrimitiveToInt(obj["cu"]),
                            ci = jsonPrimitiveToInt(obj["ci"])
                        )
                    )
                }
            }
            if (byProductId.isEmpty()) return

            for (pd in lines) {
                val row = byProductId[pd.productId] ?: continue
                row.cp?.let { pd.cp = it }
                row.cd?.let { pd.cd = it }
                row.cu?.let { pd.cu = it }
                row.ci?.let { pd.ci = it }
            }
        }
    }
}


